/**
 * Painel "Simular venda": quantos ambientes, para quando, e a resposta em
 * uma frase (cabe com folga / falar com o PCP / não cabe), com os motivos,
 * o prazo de fluxo, a ocupação antes × depois e quem seria empurrado.
 *
 * Não grava nada. A entrada vai para a URL a cada mudança (o link copiado
 * abre a mesma simulação); a conta é refeita na hora, no navegador.
 */
import React, { useEffect, useState } from "react";

import BotaoCopiar from "../../components/BotaoCopiar";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useToast } from "../../context/ToastContext";
import { MAX_AMBIENTES_SIMULADOS } from "../../utils/capacidade";
import { dataCompleta, ddmm } from "./apresentacao";
import {
  CLASSES_NA_TELA,
  VEREDITO_NA_TELA,
  linhasDoFluxo,
  textoDaEntrada,
  textoDoMotivo,
  textoIndisponivel,
  textoSegundaLeituraDaSimulacao,
} from "./apresentacaoSimulacao";
import { ListaEmpurrados, OcupacaoAntesDepois } from "./ResultadoComparacao";
import { Aviso, BotaoTexto, Regua } from "./styles";
import {
  CorpoPainel,
  Formulario,
  LinhaBotoes,
  ListaClasses,
  ListaComPonto,
  Secao,
  Tabela,
  Veredito,
} from "./stylesSimulacao";

const CLASSE_PADRAO = "normal";

function formularioDe(entrada) {
  if (!entrada) return { quantidade: "1", entrega: "", classes: [CLASSE_PADRAO], detalhar: false };
  const classes = entrada.ambientes.map((a) => a.classe);
  return {
    quantidade: String(classes.length),
    entrega: entrada.entregaDesejada,
    classes,
    detalhar: classes.some((c) => c !== CLASSE_PADRAO),
  };
}

function redimensionar(classes, n) {
  return Array.from({ length: n }, (_, i) => classes[i] || CLASSE_PADRAO);
}

/** `{entrada}` válida ou `{erro}` com o que corrigir. */
function validar(form, hoje) {
  const n = Number(form.quantidade);
  if (!Number.isInteger(n) || n < 1 || n > MAX_AMBIENTES_SIMULADOS) {
    return { erro: `Informe de 1 a ${MAX_AMBIENTES_SIMULADOS} ambientes.` };
  }
  if (!form.entrega) return { erro: null };
  if (form.entrega < hoje) return { erro: "A entrega precisa ser de hoje em diante." };
  return {
    entrada: {
      ambientes: redimensionar(form.classes, n).map((classe) => ({ classe })),
      entregaDesejada: form.entrega,
    },
  };
}

function Formularios({ form, setForm, hoje, erro }) {
  const n = Number(form.quantidade);
  const mostrarClasses = form.detalhar && Number.isInteger(n) && n >= 1 && n <= MAX_AMBIENTES_SIMULADOS;
  const alterarClasse = (indice, classe) =>
    setForm((atual) => {
      const classes = redimensionar(atual.classes, n);
      classes[indice] = classe;
      return { ...atual, classes };
    });
  return (
    <Secao aria-label="O que simular">
      <Formulario>
        <label>
          Ambientes
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_AMBIENTES_SIMULADOS}
            value={form.quantidade}
            onChange={(e) => setForm((atual) => ({ ...atual, quantidade: e.target.value }))}
          />
        </label>
        <label>
          Entrega desejada
          <input
            type="date"
            min={hoje}
            value={form.entrega}
            onChange={(e) => setForm((atual) => ({ ...atual, entrega: e.target.value }))}
          />
        </label>
      </Formulario>
      {erro ? <p role="alert">{erro}</p> : null}
      <BotaoTexto
        type="button"
        aria-expanded={form.detalhar}
        onClick={() => setForm((atual) => ({ ...atual, detalhar: !atual.detalhar }))}
      >
        {form.detalhar ? "Esconder a classe de cada ambiente" : "Algum ambiente é leve ou pesado?"}
      </BotaoTexto>
      {mostrarClasses ? (
        <>
          <ListaClasses>
            {redimensionar(form.classes, n).map((classe, i) => (
              // A posição é a identidade: os ambientes simulados não têm nome.
              <li key={i}>
                <span>Ambiente {i + 1}</span>
                <Formulario as="span">
                  <select
                    aria-label={`Classe do ambiente ${i + 1}`}
                    value={classe}
                    onChange={(e) => alterarClasse(i, e.target.value)}
                  >
                    {CLASSES_NA_TELA.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.rotulo}
                      </option>
                    ))}
                  </select>
                </Formulario>
              </li>
            ))}
          </ListaClasses>
          <Regua>
            Leve vale meio ambiente e pesado vale dois na conta com as exceções marcadas pelo PCP. Enquanto as marcas
            não cobrem o histórico, a conta oficial é a de ambientes (cada um vale 1) e a dos pesos aparece à parte.
          </Regua>
        </>
      ) : null}
    </Secao>
  );
}

function PrazoDeFluxo({ fluxo }) {
  const linhas = linhasDoFluxo(fluxo);
  return (
    <Secao aria-label="Prazo de fluxo">
      <h3>Prazo de fluxo: da venda ao carregamento</h3>
      {linhas.length === 0 ? (
        <p>Sem histórico suficiente para medir (são precisos 30 ambientes carregados nas últimas 52 semanas).</p>
      ) : (
        <>
          <Tabela $colunas="auto auto" role="table" aria-label="Prazo de fluxo">
            <span className="cab">Quantos saíram</span>
            <span className="cab num">Dias úteis</span>
            <span className="cab num">Se assinar hoje</span>
            {linhas.map((l) => (
              <React.Fragment key={l.id}>
                <span className={l.destaque ? "forte" : undefined}>{l.rotulo}</span>
                <span className={`num${l.destaque ? " forte" : ""}`}>{l.du}</span>
                <span className={`num${l.destaque ? " forte" : ""}`}>{dataCompleta(l.data)}</span>
              </React.Fragment>
            ))}
          </Tabela>
          <p>
            Medido em {fluxo.n} ambientes carregados nas últimas 52 semanas. É o caminho inteiro (projeto, aprovação,
            compras, fábrica e expedição), não só a marcenaria.
          </p>
        </>
      )}
    </Secao>
  );
}

/** "Assinou?": a simulação vira o formulário de venda assinada já preenchido. */
function RegistrarComoVenda({ entrada, onRegistrarVenda }) {
  if (!onRegistrarVenda) return null;
  return (
    <Secao aria-label="Venda assinada">
      <h3>O contrato foi assinado?</h3>
      <p>
        Registre como venda assinada: ela passa a ocupar lugar na fila até o PCP digitar o pedido na planilha. O
        formulário já vem com estes ambientes e esta data; falta o cliente.
      </p>
      <LinhaBotoes>
        <BotaoTexto type="button" onClick={() => onRegistrarVenda(entrada)}>
          Registrar como venda assinada
        </BotaoTexto>
      </LinhaBotoes>
    </Secao>
  );
}

function Resultado({ resultado, entrada, link, onErroCopia, onRegistrarVenda }) {
  if (resultado.erroEntrada) {
    return (
      <Aviso $tom="warning" role="note">
        {resultado.erroEntrada}
      </Aviso>
    );
  }
  if (resultado.indisponivel) {
    return (
      <Aviso $tom="warning" role="note">
        {textoIndisponivel(resultado)}
      </Aviso>
    );
  }
  const veredito = VEREDITO_NA_TELA[resultado.veredito];
  const { termino, alvo, G, posicoes } = resultado.capacidade;
  return (
    <>
      <Veredito $tom={veredito.tom} aria-live="polite" data-veredito={resultado.veredito}>
        <span className="frase">
          <i aria-hidden="true" />
          {veredito.frase}
        </span>
        <span className="entrada">{textoDaEntrada(entrada)}</span>
      </Veredito>
      <Secao aria-label="Motivos">
        <h3>Por quê</h3>
        <ListaComPonto>
          {resultado.motivos.map((m) => (
            <li key={m.codigo}>
              <i data-tom={m.veredito ? VEREDITO_NA_TELA[m.veredito].tom : "neutral"} aria-hidden="true" />
              <span>{textoDoMotivo(m.codigo, resultado)}</span>
            </li>
          ))}
        </ListaComPonto>
      </Secao>
      <Secao aria-label="Na fila">
        <h3>Na fila da marcenaria</h3>
        <p>
          A venda entra {posicoes.primeira === posicoes.ultima ? `na posição ${posicoes.primeira}` : `nas posições ${posicoes.primeira} a ${posicoes.ultima}`}{" "}
          e a fila abre vaga para ela entre {ddmm(termino.min)} e {ddmm(termino.max)} (mais provável {ddmm(termino.central)}). É a
          vaga na marcenaria, não a data de término: antes dela ainda vêm projeto, aprovação e compras (veja o prazo de fluxo).
          Para entregar em {ddmm(entrada.entregaDesejada)} ela precisa terminar até {ddmm(alvo)}
          {G > 0 ? `: ${G} dias úteis antes, a folga medida entre o término e o carregamento.` : "."}
        </p>
      </Secao>
      {resultado.segundaLeitura ? (
        <Secao aria-label="Com as exceções marcadas">
          <h3>Com as exceções marcadas</h3>
          <p>{textoSegundaLeituraDaSimulacao(resultado)}</p>
        </Secao>
      ) : null}
      <PrazoDeFluxo fluxo={resultado.fluxo} />
      <OcupacaoAntesDepois ocupacao={resultado.ocupacao} />
      <ListaEmpurrados empurrados={resultado.empurrados} />
      <Secao aria-label="Compartilhar">
        <h3>Compartilhar</h3>
        <LinhaBotoes>
          <BotaoCopiar texto={link} rotulo="Copiar link" rotuloCopiado="Link copiado" onErro={onErroCopia} />
        </LinhaBotoes>
        <p>O link leva só a quantidade, a classe dos ambientes e a data: nada de cliente nem de valor.</p>
      </Secao>
      <RegistrarComoVenda entrada={entrada} onRegistrarVenda={onRegistrarVenda} />
    </>
  );
}

/**
 * `aberto`, `onFechar`; `entrada`/`resultado`/`link`/`linkInvalido` vêm de
 * `useSimulacao`; `onDefinir(entrada)` grava a entrada nova na URL;
 * `onRegistrarVenda(entrada)` (só para quem registra venda assinada) abre o
 * registro já preenchido com a simulação.
 */
export default function SimularVenda({
  aberto,
  onFechar,
  entrada,
  resultado,
  link,
  linkInvalido,
  onDefinir,
  onRegistrarVenda = null,
  hoje,
}) {
  const toast = useToast();
  const [form, setForm] = useState(() => formularioDe(entrada));
  const { entrada: valida, erro } = validar(form, hoje);
  const codigoValido = valida ? JSON.stringify(valida) : null;

  // Abriu de novo: o formulário volta a mostrar o que está na URL.
  useEffect(() => {
    if (aberto) setForm(formularioDe(entrada));
    // Só na abertura: depois disso quem manda é o formulário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  // Só grava na URL o que mudou: regravar o mesmo valor renavegaria à toa.
  const codigoNaUrl = entrada ? JSON.stringify(entrada) : null;
  useEffect(() => {
    if (aberto && codigoValido && codigoValido !== codigoNaUrl) onDefinir(JSON.parse(codigoValido));
  }, [aberto, codigoValido, codigoNaUrl, onDefinir]);

  const mostrarResultado = Boolean(valida && resultado && entrada);
  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Simular venda"
      subtitle="Cabe na fila da fábrica? Nada é gravado."
      width="min(94vw, 560px)"
      ariaLabelledBy="simular-venda-titulo"
    >
      <CorpoPainel>
        {linkInvalido ? (
          <Aviso $tom="warning" role="note">
            O link de simulação está incompleto ou é de outra versão. Preencha de novo abaixo.
          </Aviso>
        ) : null}
        <Formularios form={form} setForm={setForm} hoje={hoje} erro={erro} />
        {mostrarResultado ? (
          <Resultado
            resultado={resultado}
            entrada={entrada}
            link={link}
            onErroCopia={(e) => toast?.error?.(e?.message || "Não foi possível copiar o link.")}
            onRegistrarVenda={onRegistrarVenda}
          />
        ) : (
          <Regua>Escolha a data de entrega desejada para ver se cabe.</Regua>
        )}
      </CorpoPainel>
    </SlideOverPanel>
  );
}
